import React from "react";
import {
    Typography,
    Button,
    Input,
    Avatar,
    Tabs,
    TabsHeader,
    Tab,
    IconButton,
    Tooltip
} from "@material-tailwind/react";
import Swal from "sweetalert2"
import Toaster from "../../components/Toaster";
import { Link, useParams } from "react-router-dom";
import Pagination from "../../components/Pagination";
import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Popover } from 'react-tiny-popover';
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import ShowLoader from "@/components/Loader";
import useHasPermission from "../../components/useHasPermission";
import { ArrowDownTrayIcon, ChevronDownIcon, EllipsisVerticalIcon, EyeIcon, CheckBadgeIcon, FunnelIcon, MagnifyingGlassIcon, PencilSquareIcon, TrashIcon, XCircleIcon, InformationCircleIcon, ArrowLeftIcon } from "@heroicons/react/24/solid";
import { setSelectedCustomer, approveCustomer, deleteCustomer, getCustomerMembers, bulkActionUpdate } from "../../store/slices/customerSlice";
import ScrollToTop from "@/components/ScrollToTop";
import { useTranslation } from "react-i18next";

export function CustomerMember({ type = 'customer' }) {
    const { t } = useTranslation("customer");
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const page_title = type.replace('_', ' ');
    const [activeTab, setActiveTab] = useState("all");
    const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
    const [isCheckedItemPopoverOpen, setIsCheckedItemPopoverOpen] = useState(false);
    const [checkedAction, setCheckedAction] = useState('');
    const [checkedItems, setCheckedItems] = useState([]);
    const page_url = type.replace(' ', '-')?.toLocaleLowerCase();
    const hasAddPermission = useHasPermission(`${page_url}.add`);
    const hasViewPermission = useHasPermission(`${page_url}.view`);
    const hasUpdatePermission = useHasPermission(`${page_url}.update`);
    const hasDeletePermission = useHasPermission(`${page_url}.delete`);
    const hasMemberViewPermission = useHasPermission(`member.view`);

    const action_options1 = [
        {
            label: t('active'),
            value: 'active'
        },
        {
            label: t('inactive'),
            value: 'inactive'
        },
    ];
    const action_options2 = [
        {
            label: t('approve'),
            value: 'approved'
        },
        {
            label: t('reject'),
            value: 'rejected'
        },
    ];
    const filter_options1 = [
        {
            label: t('active'),
            value: 'active'
        },
        {
            label: t('inactive'),
            value: 'inactive'
        },
    ];

    const filter_options2 = [
        {
            label: t('pending'),
            value: 'pending'
        },
        {
            label: t('rejected'),
            value: 'rejected'
        },
    ];


    useEffect(() => {
        dispatch(getCustomerMembers({ id: id, type: type, activetab: activeTab, page: currentPage, perPage: itemsPerPage, search: search, filter: filter }));
    }, [dispatch, currentPage, itemsPerPage, type]);

    useEffect(() => {
        setFilter('');
        setSearch('');
        setCurrentPage(1);
        setItemsPerPage(10);
        setActiveTab('all');

        if (hasDeletePermission) {
            action_options1.push({
                label: t('delete'),
                value: 'deleted'
            });
        }
    }, [type, hasDeletePermission]);

    useEffect(() => {
        setFilter('');
        setSearch('');
        setCurrentPage(1);
        setItemsPerPage(10);
        dispatch(getCustomerMembers({ id: id, type: type, activetab: activeTab, page: currentPage, perPage: itemsPerPage, search: '', filter: '' }));
    }, [activeTab]);

    const { list_customer_members, loading, list_loading } = useSelector((state) => state.customer);

    const handelSearch = (event) => {
        const value = event.target.value;

        if (event.key === "Enter" && value) {
            dispatch(getCustomerMembers({ id: id, type: type, activetab: activeTab, page: 1, perPage: itemsPerPage, search: search, filter: filter }));
        } else if (event.key === "Backspace" && value.length === 0) {
            dispatch(getCustomerMembers({ id: id, type: type, activetab: activeTab, page: 1, perPage: itemsPerPage, search: '', filter: filter }));
        }
    }

    const handleSearchClick = () => {
        dispatch(getCustomerMembers({ id: id, type: type, activetab: activeTab, page: 1, perPage: itemsPerPage, search: search, filter: filter }));
    }

    const handelRemoveSearch = () => {
        setSearch('');
        dispatch(getCustomerMembers({ id: id, type: type, activetab: activeTab, page: 1, perPage: itemsPerPage, search: '', filter: filter }));
    }

    const handelFilter = (value) => {
        setFilter(value);
        dispatch(getCustomerMembers({ id: id, type: type, activetab: activeTab, page: 1, perPage: itemsPerPage, search: search, filter: value }));
    }

    const handelCheckedItems = (action) => {
        setCheckedAction(action);
        setIsCheckedItemPopoverOpen(false);
        if (checkedItems.length) {
            Swal.fire({
                title: t('are_you_sure'),
                text: t('do_you_really_want_to_perform_this_action'),
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                cancelButtonColor: "#d33",
                confirmButtonText: t('yes')
            }).then((result) => {
                if (result.isConfirmed) {
                    dispatch(bulkActionUpdate({ status: action, ids: checkedItems })).unwrap().then(() => {
                        const pastTenseMap = {
                            'active': 'activated',
                            'inactive': 'inactivated',
                            'approved': 'approved',
                            'rejected': 'rejected',
                            'deleted': 'deleted',
                        };
                        const pastTenseMsg = pastTenseMap[action] ?? action + "ed";
                        Toaster.success(`${type} ${pastTenseMsg} ${t('successfully')}`);
                        setCheckedItems([]);
                        setCheckedAction('');
                        dispatch(getCustomerMembers({ id: id, type: type, activetab: activeTab, page: currentPage, perPage: itemsPerPage, search: search, filter: filter }));
                    }).catch((error) => {
                        Toaster.error(t('failed_to_perform_action'));
                    });
                }
            });
        } else {
            Toaster.error(t('you_need_to_select_an_item_to_perform_this_action'));
        }
    }

    const handleSingleCheck = (value) => {
        if (checkedItems.includes(value)) {
            setCheckedItems(checkedItems.filter((v) => v !== value));
        } else {
            setCheckedItems([...checkedItems, value]);
        }
    };

    const handleCheckAll = (e) => {
        if (e.target.checked) {
            const ids = list_customer_members?.users?.map((row) => row.id) || [];
            setCheckedItems(ids);
        } else {
            setCheckedItems([]);
        }
    };

    const isAllChecked =
        list_customer_members?.users?.length > 0 &&
        checkedItems.length === list_customer_members?.users.length;

    const handelEdit = (id, mode = "edit") => {
        if (id) {
            const customer = list_customer_members?.users?.find((row) => String(row.id) === String(id));
            dispatch(setSelectedCustomer(customer));
            navigate(`/dashboard/customer-members/${mode}/${id}`);
        }
    }

    const delete_row = (id) => {
        Swal.fire({
            title: t('are_you_sure'),
            text: t('you_wont_be_able_to_revert_this'),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: t('yes_delete_it')
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteCustomer(id)).unwrap().then(() => {
                    Toaster.success(`${page_title} ${t('deleted_successfully')}`);
                    dispatch(getCustomerMembers({ id: id, type: type, activetab: activeTab, page: currentPage, perPage: itemsPerPage, search: search, filter: filter }));
                }).catch((error) => {
                    console.error("Delete failed:", error);
                    Toaster.error(t('delete_failed'));
                });
            }
        });
    }

    const [isPopoverOpen, setIsPopoverOpen] = useState([]);
    const openActionPopover = (id) => {
        const key = id;
        setIsPopoverOpen((prev) => {
            if (prev.includes(key)) {
                return [];
            } else {
                return [key];
            }
        });
    };

    const popoverRef = useRef(null);
    useEffect(() => {
        function handleClickOutside(e) {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) {
                setIsPopoverOpen([]);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const approveRejectCustomer = async (id, value) => {
        var msg = value == 'approved' ? 'approve' : value == 'rejected' ? 'reject' : value;
        let actionVerbs = ["approve", "reject"];
        let text;
        if (actionVerbs.includes(msg.toLowerCase())) {
            text = t('do_you_want_to_action_this_user', { action: msg, user: type.toLowerCase() });
        } else {
            text = t('do_you_want_to_make_this_user_status', { status: msg, user: type.toLowerCase() });
        }
        Swal.fire({
            title: t('are_you_sure'),
            text: `${text}`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: t('yes')
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(approveCustomer({ id, value })).unwrap().then((res) => {
                    const pastTenseMap = {
                        'active': 'activated',
                        'inactive': 'inactivated',
                        'approved': 'approved',
                        'rejected': 'rejected',
                    };
                    const pastTenseMsg = pastTenseMap[res.status] ?? res.status + "ed";

                    Toaster.success(`${type} ${pastTenseMsg} ${t('successfully')}`);
                    dispatch(getCustomerMembers({ id: id, type: type, activetab: activeTab, page: currentPage, perPage: itemsPerPage, search: search, filter: filter }));
                }).catch((error) => {
                    console.error("Changes failed:", error);
                    Toaster.error(t('changes_failed'));
                });
                setIsPopoverOpen([]);
            }
        });
    };

    return (
        <div className="mt-5 mb-8 flex flex-col gap-8">
            {loading && <ShowLoader />}
            <ScrollToTop />
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0 px-4 md:px-6">
                <div className="w-full md:flex-1 flex items-center gap-1">
                    <Link to={`/dashboard/${type}s`} className="flex items-center text-blue-600 hover:text-blue-800">
                        <ArrowLeftIcon className="h-4 w-4 mr-1" />
                    </Link>
                    <Typography variant="h5" color="blue-gray">
                        {t('customer_members', { type: type })} ({list_customer_members?.pagination?.total || 0})
                    </Typography>
                </div>
                <div className="w-full md:flex-1 flex justify-center">
                    <div className="w-full max-w-md relative">
                        <Input className={search ? 'pr-10' : ''} label={t('search')} icon={!search ? <MagnifyingGlassIcon className="h-5 w-5" /> : ''} value={search} onChange={(e) => setSearch(e.target.value)} onKeyUp={handelSearch} />
                        {search && (
                            <IconButton variant="text" size="sm" className="!absolute right-1 top-1" onClick={handelRemoveSearch}>
                                <XCircleIcon className="h-5 w-5 text-red-500" />
                            </IconButton>
                        )}
                    </div>
                    {search && (<Button variant="outlined" className="flex items-center border-gray-300 capitalize font-bold bg-black text-white mx-2"
                        onClick={handleSearchClick}
                    ><MagnifyingGlassIcon className="w-4 h-4 mr-1" /> {t('search')}</Button>)}
                </div>

            </div>
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0 px-4 md:px-6">
                <div className="w-full md:w-auto relative">
                    <div className="flex bg-gray-100 rounded-2xl p-1 shadow-sm relative">

                        <button
                            onClick={() => setActiveTab("all")}
                            className={`relative text-sm font-semibold px-6 py-2 rounded-2xl transition-colors duration-300 z-10
          ${activeTab === "all" ? "text-white" : "text-gray-700 hover:text-black"}`}
                        >
                            {t('existing_members')} {activeTab !== "all" && <span className="text-gray-400 text-xs ml-1">({list_customer_members?.totalExisting})</span>}
                            {activeTab === "all" && (
                                <span className="absolute inset-0 bg-black rounded-2xl z-[-1] transition-all duration-300"></span>
                            )}
                        </button>

                        <button
                            onClick={() => setActiveTab("new")}
                            className={`relative text-sm font-semibold px-6 py-2 rounded-2xl transition-colors duration-300 z-10
          ${activeTab === "new" ? "text-white" : "text-gray-700 hover:text-black"}`}
                        >
                            {t('new_request')} {activeTab !== "new" && <span className="text-gray-400 text-xs ml-1">({list_customer_members?.totalNewRequest})</span>}
                            {activeTab === "new" && (
                                <span className="absolute inset-0 bg-black rounded-2xl z-[-1] transition-all duration-300"></span>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0 px-4 md:px-6">
                <div className="w-full md:flex-1">
                    <Typography variant="h6" color="blue-gray">
                        {t('members', { count: list_customer_members?.pagination?.total || 0 })}
                    </Typography>
                </div>
                <div className="flex w-full md:w-auto items-center gap-3 flex-wrap justify-end md:justify-end">
                    <Popover
                        isOpen={isFilterPopoverOpen}
                        positions={["right", "bottom", "left"]}
                        padding={4}
                        onClickOutside={() => setIsFilterPopoverOpen(false)}
                        content={
                            <div className="bg-white shadow-xl rounded-md w-48 flex flex-col z-50 border border-gray-100 py-1">
                                <button
                                    onClick={() => {
                                        handelFilter("");
                                        setIsFilterPopoverOpen(false);
                                    }}
                                    className={`text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 ${filter === '' ? 'bg-gray-100 font-semibold' : ''}`}
                                >
                                    {t('all')}
                                </button>
                                {activeTab === 'all' && filter_options1?.map((option) => (
                                    <button
                                        key={option.value}
                                        onClick={() => {
                                            handelFilter(option.value);
                                            setIsFilterPopoverOpen(false);
                                        }}
                                        className={`text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 ${filter === option.value ? 'bg-gray-100 font-semibold' : ''}`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                                {activeTab === 'new' && filter_options2?.map((option) => (
                                    <button
                                        key={option.value}
                                        onClick={() => {
                                            handelFilter(option.value);
                                            setIsFilterPopoverOpen(false);
                                        }}
                                        className={`text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 ${filter === option.value ? 'bg-gray-100 font-semibold' : ''}`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        }
                    >
                        <Button
                            onClick={() => setIsFilterPopoverOpen(!isFilterPopoverOpen)}
                            variant="outlined"
                            className="flex items-center gap-2 border-gray-300 text-gray-800 capitalize font-medium"
                        >
                            <FunnelIcon className="h-4 w-4" /> {t('filter')} {filter && `(${filter})`}
                        </Button>
                    </Popover>
                    {hasUpdatePermission && <Popover
                        isOpen={isCheckedItemPopoverOpen}
                        positions={["bottom"]}
                        padding={4}
                        onClickOutside={() => setIsCheckedItemPopoverOpen(false)}
                        content={
                            <div className="bg-white shadow-xl rounded-md w-48 flex flex-col z-50 border border-gray-100 py-1">
                                {activeTab === 'all' && action_options1?.map((option) => (
                                    <button
                                        key={option.value}
                                        onClick={() => {
                                            handelCheckedItems(option.value);
                                        }}
                                        className="text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 capitalize"
                                    >
                                        {option.label}
                                    </button>
                                ))}
                                {activeTab === 'new' && action_options2?.map((option) => (
                                    <button
                                        key={option.value}
                                        onClick={() => {
                                            handelCheckedItems(option.value);
                                        }}
                                        className="text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2 capitalize"
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        }
                    >
                        <Button
                            onClick={() => setIsCheckedItemPopoverOpen(!isCheckedItemPopoverOpen)}
                            variant="outlined"
                            className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white"
                        >
                            {t('selected_items')} {checkedAction && `(${checkedAction})`} <ChevronDownIcon className="h-4 w-4" />
                        </Button>
                    </Popover>}
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full min-w-[800px] table-auto text-left">
                    <thead>
                        {type === 'Customer' && <tr>
                            {[t('id', { type: type }), t('company_name'), t('contact_number'), t('business_type'), t('users'), t('rfqs'), t('orders'), t('last_ordered'), t('status'), ''].map((el) => (
                                <th
                                    key={el}
                                    className="border-b border-blue-gray-50 py-3 px-5 text-left"
                                >
                                    {el == t('id', { type: type }) ? (
                                        <div className="flex items-center gap-4">
                                            {hasUpdatePermission && <input
                                                type="checkbox"
                                                className="w-4 h-4 accent-green cursor-pointer"
                                                onChange={(e) => handleCheckAll(e)}
                                                checked={isAllChecked}
                                            />}
                                            <Typography
                                                variant="small"
                                                className="text-[11px] font-bold text-black-gray"
                                            >
                                                {el}
                                            </Typography>
                                        </div>
                                    ) : (
                                        <Typography
                                            variant="small"
                                            className="text-[11px] font-bold text-black-gray"
                                        >
                                            {el}
                                        </Typography>
                                    )}
                                </th>
                            ))}
                        </tr>}
                        {type === 'Vendor' && <tr>
                            {[t('id', { type: type }), t('company_name'), t('contact_person'), t('contact_number'), t('vendor_type'), t('email'), t('location'), t('product_category'), t('status'), ''].map((el) => (
                                <th
                                    key={el}
                                    className="border-b border-blue-gray-50 py-3 px-5 text-left"
                                >
                                    {el == t('id', { type: type }) ? (
                                        <div className="flex items-center gap-4">
                                            {hasUpdatePermission && <input
                                                type="checkbox"
                                                className="w-4 h-4 accent-green cursor-pointer"
                                                onChange={(e) => handleCheckAll(e)}
                                                checked={isAllChecked}
                                            />}
                                            <Typography
                                                variant="small"
                                                className="text-[11px] font-bold text-black-gray"
                                            >
                                                {el}
                                            </Typography>
                                        </div>
                                    ) : (
                                        <Typography
                                            variant="small"
                                            className="text-[11px] font-bold text-black-gray"
                                        >
                                            {el}
                                        </Typography>
                                    )}
                                </th>
                            ))}
                        </tr>}
                    </thead>
                    <tbody>
                        {list_loading ? <tr><td colSpan={10} className="text-center"> <div role="status">
                            <svg aria-hidden="true" className="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-green-500" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                            </svg>
                        </div></td></tr> : ''}

                        {list_customer_members?.users?.length > 0 ? list_customer_members?.users.map(
                            ({ id, company_name, first_name, last_name, mobile_number, country_code, email, vendor_business_type_name, permanent_address, status, member_count }, key) => {
                                const className = `py-3 px-5 ${key === list_customer_members?.users.length - 1
                                    ? ""
                                    : "border-b border-blue-gray-50"
                                    }`;

                                return (
                                    <tr key={id}>
                                        <td className={className}>
                                            <div className="flex items-center gap-4">
                                                {hasUpdatePermission && <input type="checkbox"
                                                    className="w-4 h-4 accent-green cursor-pointer"
                                                    onChange={() => handleSingleCheck(id)}
                                                    checked={checkedItems.includes(id)}
                                                />}
                                                <Typography
                                                    variant="small"
                                                    color="blue-gray"
                                                    className="font-semibold"
                                                >
                                                    {t('id', { type: '' })} {String(id).padStart(4, "0")}
                                                </Typography>
                                            </div>
                                        </td>
                                        <td className={className}>
                                            <div className="flex items-center gap-1">
                                                <Typography className="text-xs font-semibold text-blue-gray-600">
                                                    {company_name}
                                                </Typography>
                                            </div>
                                        </td>
                                        {type === 'Vendor' && <td className={className}>
                                            <Typography className="text-xs font-semibold text-blue-gray-600">
                                                {first_name + ' ' + last_name}
                                            </Typography>
                                        </td>}
                                        <td className={className}>
                                            <Typography className="text-xs font-semibold text-blue-gray-600">
                                                {country_code} {mobile_number}
                                            </Typography>
                                        </td>
                                        <td className={className}>
                                            <Typography className="text-xs font-semibold text-blue-gray-600">
                                                {vendor_business_type_name}
                                            </Typography>
                                        </td>
                                        {type === 'Vendor' && <>
                                            <td className={className}>
                                                <Typography className="text-xs font-semibold text-blue-gray-600">
                                                    {email}
                                                </Typography>
                                            </td><td className={className}>
                                                <Typography className="text-xs font-semibold text-blue-gray-600">
                                                    {permanent_address}
                                                </Typography>
                                            </td><td className={className}>
                                                <Typography className="text-xs font-semibold text-blue-gray-600">
                                                    _
                                                </Typography>
                                            </td>
                                        </>}
                                        {type === 'Customer' && <>
                                            <td className={className}>
                                                <Typography className="text-xs font-semibold text-blue-gray-600">
                                                    {member_count > 0 ? (
                                                        <Link to={`/dashboard/customer-members/${id}`} className="text-blue-500 hover:underline">{member_count}</Link>
                                                    ) : (
                                                        0
                                                    )}
                                                </Typography>
                                            </td>
                                            <td className={className}>
                                                <Typography className="text-xs font-semibold text-blue-gray-600">
                                                    _
                                                </Typography>
                                            </td>
                                            <td className={className}>
                                                <Typography className="text-xs font-semibold text-blue-gray-600">
                                                    _
                                                </Typography>
                                            </td>
                                            <td className={className}>
                                                <Typography className="text-xs font-semibold text-blue-gray-600">
                                                    _
                                                </Typography>
                                            </td>
                                        </>}
                                        <td className={className}>
                                            <span
                                                className={`inline-flex items-center rounded-xl px-2 py-1 text-xs font-medium inset-ring inset-ring-green-600/20
                            ${status === 'pending'
                                                        ? 'bg-yellow-50 text-yellow-800'
                                                        : status === 'active'
                                                            ? 'bg-green-50 text-green-700'
                                                            : 'bg-red-50 text-red-700'
                                                    }`}
                                            >
                                                {status ? t(status.toLowerCase()) : ""}

                                            </span>

                                        </td>
                                        <td className={className}>
                                            <div className="flex items-center space-x-2">
                                                <Popover
                                                    isOpen={isPopoverOpen.includes(id)}
                                                    positions={['bottom', 'left']}
                                                    padding={4}
                                                    content={<div ref={popoverRef} className="bg-white shadow-xl rounded-md w-32 flex flex-col z-50 border border-gray-100 py-1">
                                                        {hasViewPermission && <button
                                                            className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                                            onClick={() => handelEdit(id, "view")}
                                                        >
                                                            <EyeIcon className="h-4 w-4 text-blue-500"></EyeIcon>
                                                            {t('view')}
                                                        </button>}
                                                        {hasUpdatePermission && <button
                                                            className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                                            onClick={() => handelEdit(id, "edit")}
                                                        >
                                                            <PencilSquareIcon className="h-4 w-4 text-blue-500"></PencilSquareIcon>
                                                            {t('edit')}
                                                        </button>}
                                                        {hasDeletePermission && <button
                                                            className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                                            onClick={() => delete_row(id)}
                                                        >
                                                            <TrashIcon className="h-4 w-4 text-red-500"></TrashIcon>
                                                            {t('delete')}
                                                        </button>}
                                                        {hasUpdatePermission && <>
                                                            {status === "pending" &&
                                                                <>
                                                                    <button
                                                                        className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                                                        onClick={() => approveRejectCustomer(id, "approved")}
                                                                    >
                                                                        <CheckBadgeIcon className="h-4 w-4 text-blue-500" />
                                                                        {t('approve')}
                                                                    </button>
                                                                    <button
                                                                        className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                                                        onClick={() => approveRejectCustomer(id, "rejected")}
                                                                    >
                                                                        <XCircleIcon className="h-4 w-4 text-red-500" />
                                                                        {t('reject')}
                                                                    </button>
                                                                </>
                                                            }
                                                            {status === "rejected" &&
                                                                <button
                                                                    className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                                                    onClick={() => approveRejectCustomer(id, "approved")}
                                                                >
                                                                    <CheckBadgeIcon className="h-4 w-4 text-blue-500" />
                                                                    {t('approve')}
                                                                </button>
                                                            }
                                                            {status === "active" &&
                                                                <button
                                                                    className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                                                    onClick={() => approveRejectCustomer(id, "inactive")}
                                                                >
                                                                    <XCircleIcon className="h-4 w-4 text-blue-500" />
                                                                    {t('inactivate')}
                                                                </button>
                                                            }
                                                            {status === "inactive" &&
                                                                <button
                                                                    className="flex items-center gap-2 text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2"
                                                                    onClick={() => approveRejectCustomer(id, "active")}
                                                                >
                                                                    <CheckBadgeIcon className="h-4 w-4 text-blue-500" />
                                                                    {t('activate')}
                                                                </button>
                                                            }</>}
                                                    </div>}>
                                                    <IconButton variant="text" color="blue-gray" onClick={() => openActionPopover(id)}>
                                                        <EllipsisVerticalIcon className="h-6 w-6" />
                                                    </IconButton>
                                                </Popover>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }
                        ) : (
                            !list_loading && (
                                <tr>
                                    <td colSpan={10} className="text-center py-3">
                                        {t('no_data_found')}
                                    </td>
                                </tr>
                            )
                        )}
                    </tbody>
                </table>
            </div>
            {list_customer_members?.pagination && list_customer_members?.pagination.total > 0 && (
                <Pagination currentPage={currentPage} itemsPerPage={itemsPerPage} setCurrentPage={setCurrentPage} setItemsPerPage={setItemsPerPage} totalPages={list_customer_members?.pagination?.last_page} />
            )}
        </div>
    );
}

export default CustomerMember;